import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { ItemPicker } from '@/components/inventory/ItemPicker';
import { formatError } from '@/lib/errors';
import {
  useItem,
  useLocations,
  useRecordStockMovement,
  type Item,
  type Location,
} from '@/features/inventory/hooks';

/** The three operator-driven movement types this dialog records. Adjustments
 *  + cycle counts are handled by the AdjustmentDialog; production movements
 *  are posted by the manufacturing module's work-order flow. */
const MOVEMENT_TYPES = ['receipt', 'issue', 'transfer'] as const;
type MovementType = (typeof MOVEMENT_TYPES)[number];

const MOVEMENT_COPY: Record<
  MovementType,
  { label: string; needsFrom: boolean; needsTo: boolean }
> = {
  receipt: {
    label: 'Receipt',
    needsFrom: false,
    needsTo: true,
  },
  issue: {
    label: 'Issue',
    needsFrom: true,
    needsTo: false,
  },
  transfer: {
    label: 'Transfer',
    needsFrom: true,
    needsTo: true,
  },
};

interface StockMovementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Lock the dialog to a single item (e.g. launched from the item detail page). */
  itemId?: string;
  /** Default movement type to open with. */
  defaultType?: MovementType;
  onRecorded?: () => void;
}

/**
 * Record a manual stock movement — receipt, issue, or transfer. Every
 * movement is a positive base-UoM quantity; the type decides which of the
 * from / to locations is required. Every movement can carry a lot number
 * (find-or-created on submit); it is required for lot-tracked items. On-hand updates automatically via the
 * `tg_apply_stock_movement` trigger.
 */
export function StockMovementDialog({
  open,
  onOpenChange,
  itemId,
  defaultType = 'receipt',
  onRecorded,
}: StockMovementDialogProps) {
  const record = useRecordStockMovement();
  const { data: locations } = useLocations();
  const { data: lockedItem } = useItem(itemId);

  const [type, setType] = useState<MovementType>(defaultType);
  const [pickedItem, setPickedItem] = useState<Item | null>(null);
  const [fromLocationId, setFromLocationId] = useState('');
  const [toLocationId, setToLocationId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [lotNumber, setLotNumber] = useState('');
  const [memo, setMemo] = useState('');

  // The effective item is the locked one (when launched from a detail page)
  // or whatever the operator picked.
  const item = itemId ? (lockedItem ?? null) : pickedItem;
  const locationRows = useMemo<Location[]>(() => locations ?? [], [locations]);

  useEffect(() => {
    if (!open) {
      setType(defaultType);
      setPickedItem(null);
      setFromLocationId('');
      setToLocationId('');
      setQuantity('');
      setLotNumber('');
      setMemo('');
    }
  }, [open, defaultType]);

  const copy = MOVEMENT_COPY[type];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!item) {
      toast.error('Pick an item first.');
      return;
    }
    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error('Quantity must be greater than zero.');
      return;
    }
    if (copy.needsFrom && !fromLocationId) {
      toast.error('Pick the source location.');
      return;
    }
    if (copy.needsTo && !toLocationId) {
      toast.error('Pick the destination location.');
      return;
    }
    if (item.is_lot_tracked && !lotNumber.trim()) {
      toast.error('Enter the lot number for this lot-tracked item.');
      return;
    }

    try {
      await record.mutateAsync({
        itemId: item.id,
        type,
        quantity: qty,
        fromLocationId: copy.needsFrom ? fromLocationId : null,
        toLocationId: copy.needsTo ? toLocationId : null,
        lotNumber: lotNumber.trim() || null,
        memo,
      });
      toast.success(`Recorded ${copy.label.toLowerCase()} of ${qty} ${item.base_uom} ${item.sku}`);
      onOpenChange(false);
      onRecorded?.();
    } catch (err) {
      toast.error(formatError(err, 'Could not record movement.'));
    }
  };

  const submitting = record.isPending;
  const noLocations = locationRows.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Record stock movement</DialogTitle>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="movement-type">Type</Label>
              <Select value={type} onValueChange={(v) => setType(v as MovementType)}>
                <SelectTrigger id="movement-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MOVEMENT_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {MOVEMENT_COPY[t].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {itemId ? (
              <div className="space-y-1.5">
                <Label>Item</Label>
                <div className="rounded-md border border-border px-3 py-2 text-sm">
                  {item ? (
                    <span className="flex items-center gap-2">
                      <span className="font-mono text-xs text-muted-foreground">{item.sku}</span>
                      <span className="truncate">{item.name}</span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Loading…</span>
                  )}
                </div>
              </div>
            ) : (
              <ItemPicker value={pickedItem?.id ?? null} onChange={setPickedItem} autoFocus />
            )}

            {noLocations && (
              <p className="rounded-md border border-dashed border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
                No active locations yet.
              </p>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              {copy.needsFrom && (
                <div className="space-y-1.5">
                  <Label htmlFor="movement-from">From location</Label>
                  <Select value={fromLocationId} onValueChange={setFromLocationId}>
                    <SelectTrigger id="movement-from">
                      <SelectValue placeholder="Pick a location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locationRows.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name} ({loc.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {copy.needsTo && (
                <div className="space-y-1.5">
                  <Label htmlFor="movement-to">To location</Label>
                  <Select value={toLocationId} onValueChange={setToLocationId}>
                    <SelectTrigger id="movement-to">
                      <SelectValue placeholder="Pick a location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locationRows.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name} ({loc.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="movement-qty">
                  Quantity <span className="text-fresh-salmon-dark">*</span>
                </Label>
                <Input
                  id="movement-qty"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  inputMode="numeric"
                  placeholder="0"
                  className="tabular-nums"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="movement-lot">
                  Lot #{' '}
                  {item?.is_lot_tracked && <span className="text-fresh-salmon-dark">*</span>}
                </Label>
                <Input
                  id="movement-lot"
                  value={lotNumber}
                  onChange={(e) => setLotNumber(e.target.value)}
                  placeholder="e.g. L-2026-001"
                  maxLength={120}
                  className="font-mono text-xs"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="movement-memo">Memo</Label>
              <Textarea
                id="movement-memo"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder="Optional note — why this movement happened."
                rows={2}
                maxLength={1000}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || noLocations}>
              {submitting ? 'Recording…' : 'Record movement'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
